import { useToast } from "@/components/Toast";
import { api } from "@/lib/api";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, X } from "lucide-react";
import { useState } from "react";

// ============================================================
// Create Contact Dialog
// ============================================================

export function CreateContactDialog({ onClose }: { onClose: () => void }) {
	const { toast } = useToast();
	const queryClient = useQueryClient();
	const [form, setForm] = useState({
		name: "",
		firma: "",
		email: "",
		telefon: "",
		position: "",
		strasse: "",
		plz: "",
		ort: "",
		notes: "",
	});

	const mutation = useMutation({
		mutationFn: (data: Record<string, string>) => api.post("/contacts", data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["contacts"] });
			toast("Kontakt erstellt", "success");
			onClose();
		},
	});

	function set(key: string, value: string) {
		setForm((prev) => ({ ...prev, [key]: value }));
	}

	function handleSubmit(e: React.FormEvent) {
		e.preventDefault();
		if (!form.name.trim()) return;

		const payload: Record<string, string> = {};
		for (const [k, v] of Object.entries(form)) {
			if (v.trim()) payload[k] = v.trim();
		}
		mutation.mutate(payload);
	}

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
			<div className="card w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
				<div className="flex items-center justify-between mb-4">
					<h2 className="font-display text-lg">Neuer Kontakt</h2>
					<button type="button" onClick={onClose} className="btn-ghost p-1.5">
						<X size={16} />
					</button>
				</div>

				<form onSubmit={handleSubmit} className="space-y-3">
					<FormField
						label="Name *"
						value={form.name}
						field="name"
						onChange={set}
						required
					/>
					<FormField
						label="Firma"
						value={form.firma}
						field="firma"
						onChange={set}
					/>
					<FormField
						label="Position"
						value={form.position}
						field="position"
						onChange={set}
					/>
					<div className="grid grid-cols-2 gap-3">
						<FormField
							label="E-Mail"
							value={form.email}
							field="email"
							type="email"
							onChange={set}
						/>
						<FormField
							label="Telefon"
							value={form.telefon}
							field="telefon"
							onChange={set}
						/>
					</div>
					<FormField
						label="Straße"
						value={form.strasse}
						field="strasse"
						onChange={set}
					/>
					<div className="grid grid-cols-2 gap-3">
						<FormField
							label="PLZ"
							value={form.plz}
							field="plz"
							onChange={set}
						/>
						<FormField
							label="Ort"
							value={form.ort}
							field="ort"
							onChange={set}
						/>
					</div>
					<div>
						<label
							htmlFor="contact-notes"
							className="text-xs font-medium text-stone-500 block mb-1"
						>
							Notizen
						</label>
						<textarea
							id="contact-notes"
							value={form.notes}
							onChange={(e) => set("notes", e.target.value)}
							className="input min-h-[60px]"
						/>
					</div>

					{mutation.isError && (
						<p className="text-xs text-red-600">
							{mutation.error instanceof Error
								? mutation.error.message
								: "Fehler"}
						</p>
					)}

					<div className="flex justify-end gap-2 pt-2">
						<button
							type="button"
							onClick={onClose}
							className="btn-ghost text-sm"
						>
							Abbrechen
						</button>
						<button
							type="submit"
							disabled={mutation.isPending || !form.name.trim()}
							className="btn-primary text-sm disabled:opacity-40"
						>
							{mutation.isPending && (
								<Loader2 size={14} className="animate-spin inline mr-1" />
							)}
							Erstellen
						</button>
					</div>
				</form>
			</div>
		</div>
	);
}

function FormField({
	label,
	value,
	field,
	type = "text",
	onChange,
	required,
}: {
	label: string;
	value: string;
	field: string;
	type?: string;
	onChange: (k: string, v: string) => void;
	required?: boolean;
}) {
	return (
		<div>
			<label
				htmlFor={`cc-${field}`}
				className="text-xs font-medium text-stone-500 block mb-1"
			>
				{label}
			</label>
			<input
				id={`cc-${field}`}
				type={type}
				value={value}
				onChange={(e) => onChange(field, e.target.value)}
				className="input"
				required={required}
			/>
		</div>
	);
}
